import { create } from 'zustand';
import { authAPI } from '../api';
import { store } from './index';
import { setUser as setReduxUser, clearUser as clearReduxUser } from './slice/userSlice';

const getInitialUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const useAuthStore = create((set, get) => ({
  user: getInitialUser(),
  isAuthenticated: !!getInitialUser(),
  isLoading: true,
  error: null,

  setUser: (user) => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
      store.dispatch(setReduxUser(user));
    } else {
      localStorage.removeItem('user');
      store.dispatch(clearReduxUser());
    }
    set({ user, isAuthenticated: !!user });
  },

  // Trigger Central SSO Login
  login: () => {
    authAPI.login();
  },

  // Central SSO Logout
  logout: async () => {
    localStorage.removeItem('user');
    store.dispatch(clearReduxUser());
    set({
      user: null,
      isAuthenticated: false,
      error: null,
    });
    try {
      const res = await authAPI.logout();
      if (res.data?.ssoLogoutUrl) {
        window.location.href = res.data.ssoLogoutUrl;
        return;
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
    window.location.href = '/';
  },

  // Check current session from /api/auth/me
  checkAuth: async () => {
    set({ isLoading: true });
    try {
      const response = await authAPI.me();
      const userData = response.data;

      const normalized = {
        ...userData,
        id: userData.id || userData.userId,
        firstName: userData.firstname || userData.firstName,
        lastName: userData.lastname || userData.lastName,
        role: userData.clientRole || userData.role || 'machine_operator',
        clientRole: userData.clientRole || userData.role || 'machine_operator',
      };

      localStorage.setItem('user', JSON.stringify(normalized));
      store.dispatch(setReduxUser(normalized));

      set({
        user: normalized,
        isAuthenticated: true,
        isLoading: false,
      });
      return normalized;
    } catch (error) {
      localStorage.removeItem('user');
      store.dispatch(clearReduxUser());
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return null;
    }
  },

  updateProfile: async (data) => {
    try {
      const response = await authAPI.updateProfile(data);
      const updated = response.data.data;
      const current = get().user;
      const merged = { ...current, ...updated };
      localStorage.setItem('user', JSON.stringify(merged));
      store.dispatch(setReduxUser(merged));
      set({ user: merged });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Update failed' };
    }
  },

  clearError: () => set({ error: null }),
}));
